import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { LogOut, Plus, Pencil, Trash2, Mail, Check, X, ExternalLink } from "lucide-react";

const empty = {
  title: "",
  subtitle: "",
  description: "",
  location: "",
  status: "for_sale",
  price: "",
  area: "",
  bedrooms: "",
  bathrooms: "",
  features: "",
  cover_image: "",
  gallery: "",
  featured: false,
};

const ProjectForm = ({ initial, onCancel, onSaved, t }) => {
  const [form, setForm] = useState(() => ({
    ...empty,
    ...(initial || {}),
    features: initial?.features ? initial.features.join(", ") : "",
    gallery: initial?.gallery ? initial.gallery.join("\n") : "",
    bedrooms: initial?.bedrooms ?? "",
    bathrooms: initial?.bathrooms ?? "",
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        description: form.description.trim(),
        location: form.location.trim(),
        status: form.status,
        price: form.price.trim(),
        area: form.area.trim(),
        bedrooms: form.bedrooms === "" ? null : Number(form.bedrooms),
        bathrooms: form.bathrooms === "" ? null : Number(form.bathrooms),
        features: form.features
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        cover_image: form.cover_image.trim(),
        gallery: form.gallery
          .split(/\n+/)
          .map((s) => s.trim())
          .filter(Boolean),
        featured: !!form.featured,
      };
      if (initial?.id) {
        await api.put(`/admin/projects/${initial.id}`, payload);
      } else {
        await api.post("/admin/projects", payload);
      }
      onSaved();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="bg-ivory border border-charcoal/10 p-6 sm:p-10 space-y-6"
      data-testid="project-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Field label="Title*" value={form.title} onChange={set("title")} required testid="pf-title" />
        <Field label="Location*" value={form.location} onChange={set("location")} required testid="pf-location" />
        <Field label="Subtitle" value={form.subtitle} onChange={set("subtitle")} testid="pf-subtitle" />
        <div>
          <label className="overline block mb-2">Status</label>
          <select
            className="editorial-input"
            value={form.status}
            onChange={set("status")}
            data-testid="pf-status"
          >
            <option value="for_sale">For Sale</option>
            <option value="in_development">In Development</option>
            <option value="sold">Sold</option>
          </select>
        </div>
        <Field label="Price" value={form.price} onChange={set("price")} testid="pf-price" />
        <Field label="Area" value={form.area} onChange={set("area")} testid="pf-area" />
        <Field label="Bedrooms" value={form.bedrooms} onChange={set("bedrooms")} type="number" testid="pf-bedrooms" />
        <Field label="Bathrooms" value={form.bathrooms} onChange={set("bathrooms")} type="number" testid="pf-bathrooms" />
        <Field label="Cover image URL" value={form.cover_image} onChange={set("cover_image")} testid="pf-cover" className="sm:col-span-2" />
        <Field
          label="Features (comma separated)"
          value={form.features}
          onChange={set("features")}
          testid="pf-features"
          className="sm:col-span-2"
        />
        <div className="sm:col-span-2">
          <label className="overline block mb-2">Description*</label>
          <textarea
            required
            value={form.description}
            onChange={set("description")}
            rows={5}
            className="editorial-input"
            data-testid="pf-description"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="overline block mb-2">Gallery URLs (one per line)</label>
          <textarea
            value={form.gallery}
            onChange={set("gallery")}
            rows={3}
            className="editorial-input"
            data-testid="pf-gallery"
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.featured}
            onChange={set("featured")}
            data-testid="pf-featured"
          />
          Featured
        </label>
      </div>

      {err && <div className="text-destructive text-sm" data-testid="pf-error">{err}</div>}

      <div className="flex flex-wrap gap-3 pt-4 border-t border-charcoal/10">
        <button
          type="submit"
          disabled={saving}
          data-testid="pf-save"
          className="bg-charcoal text-ivory px-6 py-3 text-[0.72rem] tracking-[0.22em] uppercase hover:bg-teakwood transition-colors disabled:opacity-60"
        >
          {saving ? t("admin.saving") : t("admin.save")}
        </button>
        <button
          type="button"
          onClick={onCancel}
          data-testid="pf-cancel"
          className="border border-charcoal/20 px-6 py-3 text-[0.72rem] tracking-[0.22em] uppercase hover:border-charcoal transition-colors"
        >
          {t("admin.cancel")}
        </button>
      </div>
    </form>
  );
};

const Field = ({ label, value, onChange, type = "text", required, testid, className = "" }) => (
  <div className={className}>
    <label className="overline block mb-2">{label}</label>
    <input
      type={type}
      required={required}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="editorial-input"
    />
  </div>
);

const AdminDashboard = () => {
  const { t, lang, setLang } = useLang();
  const navigate = useNavigate();

  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("tenjara_user") || "null");
    } catch {
      return null;
    }
  })();

  const logout = () => {
    localStorage.removeItem("tenjara_token");
    localStorage.removeItem("tenjara_user");
    navigate("/admin/login");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, mRes] = await Promise.all([
        api.get("/admin/projects"),
        api.get("/admin/contact-messages"),
      ]);
      setProjects(pRes.data);
      setMessages(mRes.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("tenjara_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const onDelete = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await api.delete(`/admin/projects/${id}`);
      await load();
    } catch (_) {}
  };

  const onMessageDelete = async (id) => {
    if (!window.confirm(t("admin.confirm_delete"))) return;
    try {
      await api.delete(`/admin/contact-messages/${id}`);
      await load();
    } catch (_) {}
  };

  const onMarkRead = async (id) => {
    try {
      await api.patch(`/admin/contact-messages/${id}/read`);
      await load();
    } catch (_) {}
  };

  const unreadCount = messages.filter((m) => !m.read).length;

  return (
    <div className="min-h-screen bg-sand/30" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="bg-ivory border-b border-charcoal/10 sticky top-0 z-40">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-3">
            <span className="font-heading text-2xl tracking-tight">Tenjara</span>
            <span className="overline">Admin</span>
          </Link>
          <div className="flex items-center gap-6">
            <div className="hidden sm:block text-sm text-volcanic">
              {user?.email}
            </div>
            <div className="hidden sm:flex items-center text-[0.72rem] tracking-[0.2em] uppercase">
              <button onClick={() => setLang("en")} className={`px-2 py-1 ${lang === "en" ? "text-charcoal" : "text-charcoal/35"}`}>EN</button>
              <span className="text-charcoal/20">/</span>
              <button onClick={() => setLang("es")} className={`px-2 py-1 ${lang === "es" ? "text-charcoal" : "text-charcoal/35"}`}>ES</button>
            </div>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-[0.72rem] tracking-[0.22em] uppercase border-b border-charcoal/30 pb-1 hover:border-charcoal"
            >
              <LogOut size={14} /> {t("admin.logout")}
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 pt-10">
        <div className="flex items-end justify-between flex-wrap gap-4 border-b border-charcoal/10">
          <div className="flex gap-2">
            <TabBtn active={tab === "projects"} onClick={() => setTab("projects")} testid="tab-projects">
              {t("admin.projects")}
            </TabBtn>
            <TabBtn active={tab === "messages"} onClick={() => setTab("messages")} testid="tab-messages">
              {t("admin.messages")}
              {unreadCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[0.65rem] bg-teakwood text-ivory">
                  {unreadCount}
                </span>
              )}
            </TabBtn>
          </div>
          {tab === "projects" && !creating && !editing && (
            <button
              onClick={() => setCreating(true)}
              data-testid="admin-new-project"
              className="mb-3 inline-flex items-center gap-2 bg-charcoal text-ivory px-5 py-2.5 text-[0.72rem] tracking-[0.22em] uppercase hover:bg-teakwood transition-colors"
            >
              <Plus size={14} /> {t("admin.new_project")}
            </button>
          )}
        </div>

        {/* Panels */}
        <div className="py-10">
          {tab === "projects" && (
            <>
              {(creating || editing) ? (
                <ProjectForm
                  initial={editing}
                  onCancel={() => {
                    setCreating(false);
                    setEditing(null);
                  }}
                  onSaved={async () => {
                    setCreating(false);
                    setEditing(null);
                    await load();
                  }}
                  t={t}
                />
              ) : loading ? (
                <div className="py-20 text-center text-volcanic">Loading…</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {projects.map((p) => (
                    <div key={p.id} className="bg-ivory border border-charcoal/10" data-testid={`admin-project-${p.id}`}>
                      <div className="aspect-[16/10] bg-sand overflow-hidden">
                        {p.cover_image ? (
                          <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
                        ) : null}
                      </div>
                      <div className="p-5">
                        <div className="overline">{p.status.replace("_", " ")}</div>
                        <h3 className="mt-2 font-heading text-xl leading-tight">{p.title}</h3>
                        <p className="text-sm text-volcanic mt-1">{p.location}</p>
                        <div className="mt-4 flex gap-2">
                          <button
                            onClick={() => setEditing(p)}
                            data-testid={`edit-${p.id}`}
                            className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase border border-charcoal/20 px-3 py-2 hover:border-charcoal"
                          >
                            <Pencil size={12} /> {t("admin.edit")}
                          </button>
                          <button
                            onClick={() => onDelete(p.id)}
                            data-testid={`delete-${p.id}`}
                            className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase border border-destructive/30 text-destructive px-3 py-2 hover:bg-destructive hover:text-ivory"
                          >
                            <Trash2 size={12} /> {t("admin.delete")}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {tab === "messages" && (
            <>
              {loading ? (
                <div className="py-20 text-center text-volcanic">Loading…</div>
              ) : messages.length === 0 ? (
                <div className="py-20 text-center text-volcanic font-light">No messages yet.</div>
              ) : (
                <div className="space-y-4">
                  {messages.map((m) => (
                    <div
                      key={m.id}
                      data-testid={`message-${m.id}`}
                      className={`bg-ivory border p-6 ${m.read ? "border-charcoal/10" : "border-teakwood/40"}`}
                    >
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            {!m.read && <span className="w-2 h-2 bg-teakwood rounded-full" />}
                            <span className="font-heading text-xl">{m.name}</span>
                          </div>
                          <div className="mt-1 text-sm text-volcanic flex flex-wrap gap-x-4 gap-y-1">
                            <a href={`mailto:${m.email}`} className="hover:text-teakwood flex items-center gap-1">
                              <Mail size={12} /> {m.email}
                            </a>
                            {m.phone && <span>· {m.phone}</span>}
                            {m.subject && <span>· {m.subject}</span>}
                          </div>
                        </div>
                        <div className="text-xs text-volcanic/70">
                          {new Date(m.created_at).toLocaleString()}
                        </div>
                      </div>
                      <p className="mt-4 text-[0.95rem] text-charcoal leading-relaxed whitespace-pre-wrap">
                        {m.message}
                      </p>
                      <div className="mt-4 flex gap-2">
                        {!m.read && (
                          <button
                            onClick={() => onMarkRead(m.id)}
                            data-testid={`read-${m.id}`}
                            className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase border border-charcoal/20 px-3 py-2 hover:border-charcoal"
                          >
                            <Check size={12} /> {t("admin.mark_read")}
                          </button>
                        )}
                        <a
                          href={`mailto:${m.email}?subject=Re:%20${encodeURIComponent(m.subject || "Tenjara Lombok enquiry")}`}
                          className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase border border-charcoal/20 px-3 py-2 hover:border-charcoal"
                        >
                          <ExternalLink size={12} /> Reply
                        </a>
                        <button
                          onClick={() => onMessageDelete(m.id)}
                          data-testid={`msg-delete-${m.id}`}
                          className="inline-flex items-center gap-1 text-xs tracking-[0.2em] uppercase border border-destructive/30 text-destructive px-3 py-2 hover:bg-destructive hover:text-ivory"
                        >
                          <Trash2 size={12} /> {t("admin.delete")}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`px-5 py-3 text-[0.72rem] tracking-[0.22em] uppercase font-medium border-b-2 -mb-px transition-colors ${
      active ? "border-charcoal text-charcoal" : "border-transparent text-charcoal/50 hover:text-charcoal"
    }`}
  >
    {children}
  </button>
);

export default AdminDashboard;
